import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { withRouter } from 'react-router';
import Translate from 'react-translate-component';
import { Layout, Badge, Menu } from 'antd';
import { withAsyncActions } from 'react-async-client';
import { path, pick } from 'ramda';
import CustomScroll from 'react-custom-scroll/index.js';
import { withState } from 'recompose';
import cx from 'classnames';

import * as routes from 'constants/routes';
import { notificationInterval } from 'constants/defaults';
import { pinLeftMenu, collapseLeftMenu } from 'actions/appActions';
import { getMenuCounters, updateEvent, createEvent } from 'actions/asyncActions';
import CustomIcon from 'components/common/CustomIcon';

const MENU_WIDTH = 255;
const MENU_COLLAPSED_WIDTH = 71;
const activeClass = 'ant-menu-item-selected';

const CountersFetch = withAsyncActions(({ user }) => {
    const webPermission = !!path(['permissions', 'web'], user);

    return webPermission ? ({
        getMenuCounters: getMenuCounters
            .withOptions({
                dispatchOnMount: true,
                pollInterval: notificationInterval,
            }),
        createEvent: createEvent.withSuccessHandler(({ getMenuCounters: { refresh } }) => refresh()),
        updateEvent: updateEvent.withSuccessHandler(({ getMenuCounters: { refresh } }) => refresh())
    }) : {};
})(() => null);

@withRouter
@withAsyncActions({
    getMenuCounters,
})
@withState('unfixed', 'setUnfixed', false)
@connect(pick(['leftMenu']), { pinLeftMenu, collapseLeftMenu })
export default class LeftMenu extends Component {
    static propTypes = {
        user: PropTypes.object,
        getMenuCounters: PropTypes.object,
        leftMenu: PropTypes.object,
        collapseLeftMenu: PropTypes.func,
        unfixed: PropTypes.bool,
        setUnfixed: PropTypes.func,
    }

    renderMenuItem = (key, linkPath, iconType, exact, badgeCount) => {
        const text = [
            <CustomIcon key='icon' type={iconType} />,
            <Translate key='text' content={`menu.${key}`} />
        ];

        return (
            <Menu.Item key={key}>
                <NavLink to={linkPath} activeClassName={activeClass} exact={exact}>
                    { badgeCount ? (
                        <Badge count={badgeCount} showZero={false}>
                            {text}
                        </Badge>
                    ) : (
                        text
                    )}
                </NavLink>
            </Menu.Item>
        );
    }

    render() {
        const { user, leftMenu: { leftMenuPinned, leftMenuCollapsed }, collapseLeftMenu, unfixed, setUnfixed, getMenuCounters } = this.props;
        const { permissions } = user;
        const personInwork = path(['data', 'person', 'inwork'], getMenuCounters);
        const projectInwork = path(['data', 'project', 'inwork'], getMenuCounters);
        const notdoneCount = path(['data', 'notdone', 'count'], getMenuCounters);
        const collapsed = leftMenuCollapsed || user.needUpdatePassword;

        const onMenuClick = () => {
            if (!leftMenuPinned) {
                collapseLeftMenu(true)
            } else {
                unfixed && collapseLeftMenu(true);
            }
        };

        return (
            <Layout.Sider
                width={MENU_WIDTH}
                className={
                    cx("left-menu", {
                        "pinned": leftMenuPinned,
                        "unpinned": !leftMenuPinned,
                        "collapsed": collapsed,
                    })
                }
                collapsed={collapsed}
                collapsedWidth={MENU_COLLAPSED_WIDTH}
                collapsible
                onCollapse={(collapsed, type) => {
                    if (type === 'clickTrigger') {
                        collapseLeftMenu(collapsed);
                    }
                }}
                breakpoint='lg'
                onBreakpoint={unfixed => {
                    setUnfixed(unfixed);

                    if (unfixed) {
                        collapseLeftMenu(true);
                    }
                }}
            >
                <CustomScroll heightRelativeToParent="100%">
                { permissions && (
                    <Fragment>
                        <CountersFetch user={user} />
                        { permissions.web && (
                            <Menu mode="inline" selectable={false} onClick={onMenuClick}>
                                <Menu.ItemGroup key="main" className="m-t-10">
                                    {this.renderMenuItem('notdone', routes.main.path, 'notification', true, notdoneCount)}
                                </Menu.ItemGroup>
                                { permissions.web.request &&
                                    <Menu.ItemGroup key="requests">
                                        <Menu.Divider/>
                                        {this.renderMenuItem('requestAdd', routes.requestAdd.stringify(), 'send', true)}
                                        {this.renderMenuItem('request', routes.request.stringify(), 'case', false)}
                                    </Menu.ItemGroup>
                                }
                                { permissions.web.approvals &&
                                    <Menu.ItemGroup key="approval">
                                        <Menu.Divider/>
                                        { permissions.web.approvePeople &&
                                            this.renderMenuItem('approvalPerson', routes.approvalPerson.stringify(), 'user-group', false, personInwork)
                                        }
                                        { permissions.web.approveProjects &&
                                            this.renderMenuItem('approvalProject', routes.approvalProject.stringify(), 'case-check', false, projectInwork)
                                        }
                                    </Menu.ItemGroup>
                                }
                                { (permissions.web.project || permissions.web.projectMass) &&
                                    <Menu.ItemGroup key="projects">
                                        <Menu.Divider/>
                                        {permissions.web.project && this.renderMenuItem('project', routes.project.stringify(), 'briefcase', true)}
                                        {permissions.web.projectMass && this.renderMenuItem('projectMass', routes.projectMass.stringify(), 'briefcase', true)}
                                    </Menu.ItemGroup>
                                }
                            </Menu>
                        )}

                        { (permissions.web || permissions.education) && (
                            <Menu mode="inline" selectable={false} onClick={onMenuClick}>
                                <Menu.ItemGroup>
                                    { permissions.web && <Menu.Divider/> }
                                    {this.renderMenuItem('calendar', routes.calendarCurrent.stringify(), 'calendar', true)}
                                </Menu.ItemGroup>
                                { permissions.employee && permissions.employee.metro && <Menu.Divider/> }
                            </Menu>
                        )}

                        { permissions.employee && (permissions.employee.metroApproval || permissions.employee.metroRequest) && (
                            <Menu mode="inline" selectable={false} onClick={onMenuClick}>
                                <Menu.ItemGroup key="requestMetro">
                                    { permissions.employee.metroRequest && this.renderMenuItem('requestMetroAdd', routes.requestMetroAdd.stringify(), 'send', false)}
                                    { permissions.employee.metroRequest && this.renderMenuItem('requestMetro', routes.requestMetro.stringify(), 'case', false)}
                                    { permissions.employee.metroApproval && this.renderMenuItem('requestMetroApproval', routes.requestMetroApproval.stringify(), 'case-check', false)}
                                </Menu.ItemGroup>
                            </Menu>
                        )}

                        { permissions.education && permissions.education.office && (
                            <Menu mode="inline" selectable={false}onClick={onMenuClick}>
                                <Menu.ItemGroup key="actions">
                                    <Menu.Divider/>
                                    {this.renderMenuItem('actionVacancy', routes.actionVacancies.stringify(), 'vacancy')}
                                    {this.renderMenuItem('application', routes.applications.stringify(), 'send')}
                                    { permissions.education.approveEducation && (
                                        this.renderMenuItem('approvalApplication', routes.approvalApplications.stringify(), 'case-check')
                                    )}
                                    {this.renderMenuItem('events', routes.events.stringify(), 'case')}
                                    {this.renderMenuItem('courses', routes.courses.stringify(), 'graduation-cap')}
                                    { permissions.web.trainerEvents && (
                                        this.renderMenuItem('trainerEvents', routes.trainerEvents.stringify(), 'user-group')
                                    )}
                                    { this.renderMenuItem('news', routes.news.stringify(), 'notification') }
                                    { this.renderMenuItem('fileLibrary', routes.fileLibrary.stringify(), 'list-ul') }
                                </Menu.ItemGroup>
                            </Menu>
                        )}
                    </Fragment>
                )}
                </CustomScroll>
            </Layout.Sider>
        );
    }
}
